import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Columns as ColumnsIcon } from 'lucide-react';
import { SystemPill } from './Pill';
import './RowActionsMenu.css';

// The screen's always-shown columns (the ones that identify a row or carry
// its actions), listed ticked and greyed at the top of a column list with
// the System pill, so it's clear they're there and can't be switched off.
// `systemColumns`: array of labels. Shared by the Columns dropdown and the
// saved tab drawer's Columns section.
export function SystemColumnRows({ systemColumns, rowStyle }) {
  if (!systemColumns || !systemColumns.length) return null;
  return (
    <>
      {systemColumns.map((label) => (
        <label key={label} style={{ ...rowStyle, cursor: 'default', color: '#8a94a6' }}>
          <input type="checkbox" checked disabled />
          {label}
          <span style={{ marginLeft: 'auto' }}><SystemPill title="Always shown on this list." /></span>
        </label>
      ))}
      <div style={{ borderTop: '1px solid #e3e7ee', margin: '4px 0' }} />
    </>
  );
}

// A grey heading above the first column of each group, for screens whose
// columns carry `group` (e.g. Details / Classification / Location &
// Contact). Columns without a group get no heading. Shared by the Columns
// dropdown and the saved tab drawer's Columns section.
export function ColumnGroupHeading({ columns, index, style }) {
  const group = columns[index].group;
  if (!group || (index > 0 && columns[index - 1].group === group)) return null;
  return (
    <div style={{ fontSize: 11, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.4, ...style }}>
      {group}
    </div>
  );
}

// A saved column list holds every column the screen offered at save time:
// shown ones as their key, hidden ones as "-key". That's what tells a
// column the user hid apart from one added to the screen since.
const HIDDEN_PREFIX = '-';

const PICKER_ROW_STYLE = { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer' };

export function encodeColumns(visible, columns) {
  return [
    ...visible,
    ...columns.map((c) => c.key).filter((k) => !visible.includes(k)).map((k) => `${HIDDEN_PREFIX}${k}`),
  ];
}

// Just the shown keys of a saved list.
export function shownColumns(saved) {
  return (saved || []).filter((k) => !k.startsWith(HIDDEN_PREFIX));
}

// Keeps a saved column list in step with the columns this screen offers
// now: a key the screen didn't offer yet when the list was saved (neither
// shown nor hidden in it) starts at its own default visibility; a column
// the user hid stays hidden. Also drops any saved key this screen no longer
// defines, so a removed/renamed column doesn't linger. Lists saved before
// hidden keys were recorded have no "-key" entries, so a default-on column
// hidden in one comes back once, until the next toggle re-saves it.
function mergeWithKnown(saved, columns, defaultVisible) {
  const knownKeys = columns.map((c) => c.key);
  const mentioned = new Set(saved.map((k) => (k.startsWith(HIDDEN_PREFIX) ? k.slice(HIDDEN_PREFIX.length) : k)));
  return [
    ...shownColumns(saved).filter((k) => knownKeys.includes(k)),
    ...defaultVisible.filter((k) => !mentioned.has(k) && knownKeys.includes(k)),
  ];
}

// Per-user, per-screen column show/hide picker — persists via
// /api/column-preferences (user_column_preferences table), same
// tenant/user/screen_key shape as SavedFilterTabs' saved_filters and
// UserTabOrder. `columns`: [{ key, label }] — the optional columns this
// screen offers; always-visible columns (identity fields, actions) aren't
// part of this list at all. `defaultVisible`: array of keys shown before
// any preference has been saved. `onChange` fires with the current visible
// keys array both on initial load (once the saved preference, if any, is
// fetched) and on every change — the parent screen owns rendering, this
// component only owns the picker UI and persistence.
//
// `tabColumns` (optional, from useTabColumns) links this picker to the
// screen's SavedFilterTabs: while a saved tab with its own column set is
// selected, the picker shows and saves that tab's columns; otherwise it
// shows and saves the screen-level preference.
//
// `systemColumns` (optional): labels of the always-shown columns, listed
// above the optional ones (SystemColumnRows).
function ColumnPicker({ screenKey, columns, defaultVisible, onChange, tabColumns, systemColumns }) {
  const [screenVisible, setScreenVisible] = useState(defaultVisible);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const tabOwnColumns = tabColumns?.activeTab?.visible_columns;
  const usingTabColumns = Array.isArray(tabOwnColumns);

  const visible = useMemo(
    () => (usingTabColumns ? mergeWithKnown(tabOwnColumns, columns, defaultVisible) : screenVisible),
    // eslint-disable-next-line
    [usingTabColumns, tabOwnColumns, screenVisible],
  );

  useEffect(() => {
    axios.get('/api/column-preferences', { params: { screen_key: screenKey } })
      .then((res) => {
        const saved = res.data.data?.visible_columns;
        if (Array.isArray(saved)) {
          setScreenVisible(mergeWithKnown(saved, columns, defaultVisible));
        }
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, [screenKey]);

  useEffect(() => {
    onChange(visible);
    if (tabColumns) tabColumns.setCurrentVisible(visible);
    // eslint-disable-next-line
  }, [visible]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const save = (next) => {
    const encoded = encodeColumns(next, columns);
    if (usingTabColumns) {
      tabColumns.saveActiveTabColumns(encoded);
      return;
    }
    setScreenVisible(next);
    axios.put('/api/column-preferences', { screen_key: screenKey, visible_columns: encoded }).catch(() => {});
  };

  const toggle = (key) => save(visible.includes(key) ? visible.filter((k) => k !== key) : [...visible, key]);

  // Back to the screen's own default set — saved like any other choice, so
  // a tab with its own columns resets that tab's set.
  const isDefault = visible.length === defaultVisible.length && defaultVisible.every((k) => visible.includes(k));
  const resetToDefault = () => save(defaultVisible.filter((k) => columns.some((c) => c.key === k)));

  return (
    <div className="row-actions-menu" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 14px', background: '#fff', color: '#1976d2', border: '1px solid #1976d2', borderRadius: 6, fontWeight: 500, cursor: 'pointer' }}
      >
        <ColumnsIcon size={15} strokeWidth={2} />
        Columns
      </button>
      {open && (
        <div className="row-menu-dropdown" style={{ padding: '6px 0', minWidth: 220 }}>
          {usingTabColumns && (
            <div style={{ padding: '4px 14px 6px', fontSize: 11, color: '#8a94a6' }}>This tab's columns</div>
          )}
          <SystemColumnRows systemColumns={systemColumns} rowStyle={PICKER_ROW_STYLE} />
          {columns.map((c, i) => (
            <React.Fragment key={c.key}>
              <ColumnGroupHeading columns={columns} index={i} style={{ padding: i === 0 ? '4px 14px 2px' : '10px 14px 2px' }} />
              <label style={PICKER_ROW_STYLE}>
                <input type="checkbox" checked={visible.includes(c.key)} onChange={() => toggle(c.key)} />
                {c.label}
              </label>
            </React.Fragment>
          ))}
          <div style={{ borderTop: '1px solid #e3e7ee', margin: '4px 0' }} />
          <button
            type="button"
            onClick={resetToDefault}
            disabled={isDefault}
            title="Show this list's default columns"
            style={{ ...PICKER_ROW_STYLE, width: '100%', background: 'none', border: 'none', color: isDefault ? '#8a94a6' : '#1976d2', cursor: isDefault ? 'default' : 'pointer' }}
          >
            Reset to default
          </button>
        </div>
      )}
    </div>
  );
}

export default ColumnPicker;
